import axios from 'axios';
import * as Constants from '../components/constants';

// Regular expression patterns
const SMS_USERNAME_REGEX = /^[\p{L}\p{N}_@.-]+$/;
const SMS_USERNAME_ENGLISH_ONLY_REGEX = /^[a-zA-Z0-9_@.-]+$/;
const STANDARD_USERNAME_REGEX = /^[\p{L}\p{N}._$@,+-]+$/;
const STANDARD_USERNAME_ENGLISH_ONLY_REGEX = /^[a-zA-Z0-9._$@,+-]+$/;
const NAME_REGEX = /^[\p{L}\s\-'.]+$/;
const NAME_ENGLISH_ONLY_REGEX = /^[a-zA-Z\s\-'.]+$/;
const SMS_NAME_REGEX = /^[\p{L}\s\-']+$/;
const SMS_NAME_ENGLISH_ONLY_REGEX = /^[a-zA-Z\s\-']+$/;
const EMAIL_REGEX = /^(?=.{7,128}$)(?:[A-Za-z0-9-'+_]+\.)*(?:[A-Za-z0-9'+_-])+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+(?:[a-zA-Z]{2,63})$/i; // eslint-disable-line max-len
const PASSWORD_SPECIAL_CHARS_REGEX = /^[\w@.-]+$/;
const HAS_NUMBER_REGEX = /\d/;
const HAS_UPPERCASE_REGEX = /[A-Z]/;

// Username policies
const STANDARD_USERNAME_MAX_LENGTH = 120;
const STANDARD_USERNAME_MIN_LENGTH = 4;

// First & Last name policies
const SMS_FIRST_NAME_MAX_LENGTH = 32;
const STANDARD_FIRST_NAME_MAX_LENGTH = 40;

const SMS_LAST_NAME_MAX_LENGTH = 64;
const STANDARD_LAST_NAME_MAX_LENGTH = 40;

// Common validators
export function hasNumber(val: string) {
  return HAS_NUMBER_REGEX.test(val);
}

export function hasUppercase(val: string) {
  return HAS_UPPERCASE_REGEX.test(val);
}

export function hasSpace(val: string) {
  return val.indexOf(' ') >= 0;
}

// Username input specific validators
export function doesUsernameContainOnlyValidChars(username: string, applySMSValidation?: boolean, englishOnly?: boolean) {
  if (applySMSValidation) {
    return (
      englishOnly
      ? new RegExp(SMS_USERNAME_ENGLISH_ONLY_REGEX, 'g')
      : new RegExp(SMS_USERNAME_REGEX, 'gu')
    ).test(username);
  }

  return (
    englishOnly
    ? RegExp(STANDARD_USERNAME_ENGLISH_ONLY_REGEX, 'g')
    : RegExp(STANDARD_USERNAME_REGEX, 'gu')
  ).test(username);
}

export function isUsernameGreaterThanMaxLength(username: string) {
  return username.length > STANDARD_USERNAME_MAX_LENGTH;
}

export function isUsernameLessThanMinLength(username: string) {
  return username.length < STANDARD_USERNAME_MIN_LENGTH;
}

export function isEmailValid(email: string) {
  return EMAIL_REGEX.test(email);
}

// First name & last name input specific validators
function getFirstNameMaxChars(applySMSValidation?: boolean) {
  return applySMSValidation ? SMS_FIRST_NAME_MAX_LENGTH : STANDARD_FIRST_NAME_MAX_LENGTH;
}

function getLastNameMaxChars(applySMSValidation?: boolean) {
  return applySMSValidation ? SMS_LAST_NAME_MAX_LENGTH : STANDARD_LAST_NAME_MAX_LENGTH;
}

export function doesNameContainOnlyValidChars(name: string, applySMSValidation?: boolean, englishOnly?: boolean) {
  if (applySMSValidation) {
    return (
      englishOnly
      ? new RegExp(SMS_NAME_ENGLISH_ONLY_REGEX, 'g')
      : new RegExp(SMS_NAME_REGEX, 'gu')
    ).test(name);
  }

  return (
    englishOnly
    ? new RegExp(NAME_ENGLISH_ONLY_REGEX, 'g')
    : new RegExp(NAME_REGEX, 'gu')
  ).test(name);
}

export function isFirstNameMoreThanMaxLength(firstName: string, applySMSValidation?: boolean) {
  const firstNameMaxChars = getFirstNameMaxChars(applySMSValidation);

  return firstName.length > firstNameMaxChars;
}

export function isLastNameMoreThanMaxLength(lastName: string, applySMSValidation?: boolean) {
  const lastNameMaxChars = getLastNameMaxChars(applySMSValidation);

  return lastName.length > lastNameMaxChars;
}

// Password input specific validators
export function isPasswordLessThan5Chars(password: string) {
  return password.length < 5;
}

export function isPasswordLessThan8Chars(password: string) {
  return password.length < 8;
}

export function doesPasswordContainOnlyValidChars(password: string) {
  return PASSWORD_SPECIAL_CHARS_REGEX.test(password);
}

// Formik level validators

export const validatePassword = (password: string, passwordProfile?: string) => {
  const passwordErrors: PasswordHintErrors = {};

  if (!password) {
    passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_REQUIRED;

    return passwordErrors;
  }

  if (passwordProfile === Constants.FIVE_CHAR_PASSWORD_POLICY) {
    if (hasSpace(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_NO_SPACES;
      passwordErrors.hasSpace = true;
    }
    if (isPasswordLessThan5Chars(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_MISSING_5_CHARS;
      passwordErrors.doesNotHaveFiveLetters = true;
    }
  } else if (passwordProfile === Constants.EIGHT_CHAR_SIMPLE_PASSWORD_POLICY) {
    if (hasSpace(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_NO_SPACES;
      passwordErrors.hasSpace = true;
    }
    if (isPasswordLessThan8Chars(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_MISSING_8_CHARS;
      passwordErrors.doesNotHaveEightLetters = true;
    }
  } else {
    if (!hasUppercase(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_MISSING_UPPER_NUMBER_MINLENGTH;
      passwordErrors.doesNotHaveUpperCase = true;
    }
    if (!hasNumber(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_MISSING_UPPER_NUMBER_MINLENGTH;
      passwordErrors.doesNotHaveNumber = true;
    }
    if (!doesPasswordContainOnlyValidChars(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_NO_SPECIAL_CHARS;
      passwordErrors.hasSpecialChars = true;
    }
    if (isPasswordLessThan8Chars(password)) {
      passwordErrors.errorMessage = Constants.ERRMSG_PASSWORD_MISSING_8_CHARS;
      passwordErrors.doesNotHaveEightLetters = true;
    }
  }

  return passwordErrors.errorMessage && passwordErrors;
};

export const validateEmail = (email: string) => {
  let errorMsg;

  if (!email) {
    errorMsg = Constants.ERRMSG_EMAIL_REQUIRED;
  } else if (!isEmailValid(email)) {
    errorMsg = Constants.ERRMSG_EMAIL_ADDRESS_NOT_VALID;
  }

  return errorMsg;
};

export const validateUsername = (
  username: string,
  applySMSValidations?: boolean,
  englishOnly?: boolean,
  usernameSameAsEmail?: boolean,
) => {
  let errorMsg;

  if (!username) {
    errorMsg = usernameSameAsEmail ? Constants.ERRMSG_EMAIL_REQUIRED : Constants.ERRMSG_USERNAME_REQUIRED;
  } else {
    if (!doesUsernameContainOnlyValidChars(username, applySMSValidations, englishOnly)) {
      errorMsg = applySMSValidations
        ? Constants.ERRMSG_SMS_USERNAME_NO_SPECIAL_CHARS
        : Constants.ERRMSG_USERNAME_NO_SPECIAL_CHARS;
    }

    if (isUsernameLessThanMinLength(username)) {
      errorMsg = Constants.ERRMSG_USERNAME_MISSING_4_CHARS;
    }

    if (isUsernameGreaterThanMaxLength(username)) {
      errorMsg = Constants.ERRMSG_USERNAME_CANNOT_EXCEED_120_CHARS;
    }

    if (usernameSameAsEmail && !isEmailValid(username)) {
      errorMsg = Constants.ERRMSG_EMAIL_ADDRESS_NOT_VALID;
    }
  }

  return errorMsg;
};

export const validateFirstName = (firstName: string, applySMSValidations?: boolean, englishOnly?: boolean) => {
  let errorMsg;

  if (!firstName) {
    errorMsg = Constants.ERRMSG_FIRST_NAME_REQUIRED;
  } else {
    if (isFirstNameMoreThanMaxLength(firstName, applySMSValidations)) {
      errorMsg = applySMSValidations
        ? Constants.ERRMSG_FIRSTNAME_CANNOT_EXCEED_32_CHARS
        : Constants.ERRMSG_FIRSTNAME_CANNOT_EXCEED_40_CHARS;
    }

    if (!doesNameContainOnlyValidChars(firstName, applySMSValidations, englishOnly)) {
      errorMsg = applySMSValidations
        ? Constants.ERRMSG_SMS_FIRSTNAME_NO_SPECIAL_CHARS
        : Constants.ERRMSG_FIRSTNAME_NO_SPECIAL_CHARS;
    }
  }

  return errorMsg;
};

export const validateLastName = (lastName: string, applySMSValidations?: boolean, englishOnly?: boolean) => {
  let errorMsg;

  if (!lastName) {
    errorMsg = Constants.ERRMSG_LAST_NAME_REQUIRED;
  } else {
    if (isLastNameMoreThanMaxLength(lastName, applySMSValidations)) {
      errorMsg = applySMSValidations
        ? Constants.ERRMSG_LASTNAME_CANNOT_EXCEED_64_CHARS
        : Constants.ERRMSG_LASTNAME_CANNOT_EXCEED_40_CHARS;
    }

    if (!doesNameContainOnlyValidChars(lastName, applySMSValidations, englishOnly)) {
      errorMsg = applySMSValidations
        ? Constants.ERRMSG_SMS_LASTNAME_NO_SPECIAL_CHARS
        : Constants.ERRMSG_LASTNAME_NO_SPECIAL_CHARS;
    }
  }

  return errorMsg;
};

export const validateConsent = (consent: boolean) => {
  let errMsg = '';
  if (!consent) {
    errMsg = Constants.ERRMSG_CONSENT_REQUIRED;
  }
  return errMsg;
};

export const validateTurkeyConsent = (consent: boolean) => {
  let errMsg = '';
  if (!consent) {
    errMsg = Constants.ERRMSG_TURKEY_CONSENT_REQUIRED;
  }
  return errMsg;
};

const { loginRootUrl } = window.appAirDrop.config;

export async function checkEmailAvailability(email: string, escrow: string) {
  const response = await axios.get(
    `${loginRootUrl}${Constants.CHECK_EMAIL_AVAILABILITY_ENDPOINT}/${escrow}?email=${email}`,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      withCredentials: true,
    },
  );

  return response.data.data.exists;
}

export async function checkUsernameAvailability(username: string, clientId: string, escrow: string) {
  const response = await axios.get(
    `${loginRootUrl}${Constants.CHECK_USERNAME_AVAILABILITY_ENDPOINT}/${escrow}/${clientId}?username=${username}`,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      withCredentials: true,
    },
  );

  return response.data.data.exists;
}

export const getSocialSignIn = (config: any, serviceName: string) => {
  const { socialSignin } = config;
  const service = socialSignin && socialSignin.find((socialSigninObj: any) => socialSigninObj.service === serviceName);
  return service;
};
